import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { format } from "date-fns";

import { JournalSupportResponse } from "@/api";
import FormikTimePicker from "@/common/components/fields/formik/formik-time-picker/FormikTimePicker";
import { FormDialog } from "@/common/components/form-dialog/FormDialog";
import { JournalSupportReadOnlyRow } from "@/common/components/journal-support/JournalSupportReadOnlyRow";
import { getTodayAt1530 } from "@/common/components/journal-support/JournalSupportSection.utils";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useTranslation } from "@/translations";

import { useJournalSupportEdit } from "./hooks/useJournalSupportEdit";

interface Props {
  entry: JournalSupportResponse;
  onClose: () => void;
}

export function JournalSupportEditDialog({ entry, onClose }: Props) {
  const { t } = useTranslation();
  const { formik, handleEnd, isEnding } = useJournalSupportEdit({ entry, onSuccess: onClose });
  const { language } = useLanguageContext();

  const maxTime = getTodayAt1530();
  const currentTime = format(new Date(), "HH:mm", { locale: locales[language] });

  const title = (
    <Stack direction="row" alignItems="center" gap={1.5}>
      {t("calendar.journalSupport.editTitle")}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.25,
          py: 0.25,
          borderRadius: 10,
          border: "1px solid",
          borderColor: "success.main",
          bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
        }}
      >
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "success.main", flexShrink: 0 }} />
        <Typography variant="caption" color="success.main" fontWeight={500} lineHeight={1.5}>
          {t("calendar.journalSupport.statusActive")}
        </Typography>
      </Box>
    </Stack>
  );

  const endButton = (
    <Button
      variant="outlined"
      color="warning"
      startIcon={<AccessTimeIcon />}
      onClick={handleEnd}
      disabled={isEnding || formik.isSubmitting}
    >
      {t("calendar.journalSupport.endNowButton")}
    </Button>
  );

  return (
    <FormDialog
      title={title}
      onClose={onClose}
      formik={formik}
      submitLabel={t("calendar.journalSupport.submit")}
      submitDisabled={!formik.dirty || isEnding}
      extraAction={{ left: endButton }}
      paperSx={{ maxWidth: 640 }}
    >
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t("calendar.journalSupport.editDescription")}
      </Typography>

      <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, px: 2, mb: 3 }}>
        <JournalSupportReadOnlyRow label={t("calendar.journalSupport.userCodeLabel")} value={entry.userCode} />
        <Box sx={{ borderTop: "1px solid", borderColor: "divider" }} />
        <JournalSupportReadOnlyRow label={t("calendar.journalSupport.nameLabel")} value={entry.fullName} />
      </Box>

      <Grid container rowSpacing={2} columnSpacing={2} mb={3}>
        <Grid size={6}>
          <FormikTimePicker
            formik={formik}
            fieldName="startTime"
            label={t("calendar.journalSupport.fromLabel")}
            required
            disabled
          />
        </Grid>
        <Grid size={6}>
          <FormikTimePicker
            formik={formik}
            fieldName="endTime"
            label={t("calendar.journalSupport.toLabel")}
            helperText={t("calendar.journalSupport.toHelperEdit")}
            required
            maxTime={maxTime}
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
          border: "1px solid",
          borderColor: (theme) => alpha(theme.palette.warning.main, 0.35),
          borderRadius: 1,
          px: 2,
          py: 1.5,
        }}
      >
        <Typography variant="body2">
          <strong>{t("calendar.journalSupport.endNowButton")}</strong>{" "}
          {t("calendar.journalSupport.endNowWarning", { time: currentTime })}
        </Typography>
      </Box>
    </FormDialog>
  );
}
